import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { ContractsTabTemplate as template } from './contracts-summaries.template';
import { ContractsTabStyles as styles } from './contracts-summaries.styles';
import { Select } from '@genesislcap/foundation-zero';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../../services/enum.service';
import { Documents } from '../../../../../types';
import { IProjectModalService } from '../../../../../services';
import { clearErrorUtil, displayErrorUtil } from '../../../../../utils';

const name = 'contracts-tab';

const documentStatusDefault = CASCADE_ENUM.DOCUMENT_STATUS_DEFAULT;

@customElement({
  name,
  template,
  styles,
})
export class ContractsTab extends FASTElement {
  @observable equipmentSupplyAgreements: Select;
  @observable equipmentSupplyAgreementsValue: String = '';
  @observable epcAgreement: Select;
  @observable epcAgreementValue: String = '';
  @observable operationMaintenanceAgreement: Select;
  @observable operationMaintenanceAgreementValue: String = '';
  @observable managementServicesAgreement: Select;
  @observable managementServicesAgreementValue: String = '';
  @observable interconnectionAgreement: Select;
  @observable interconnectionAgreementValue: String = '';
  @observable bopAgreement: Select;
  @observable bopAgreementValue: String = '';
  @observable constructionAgreement: Select;
  @observable constructionAgreementValue: String = '';
  @observable energyManagementAgreement: Select;
  @observable energyManagementAgreementValue: String = '';
  @observable feasibilityStudy: Select;
  @observable feasibilityStudyValue: String = '';
  @observable systemImpactStudy: Select;
  @observable systemImpactStudyValue: String = '';
  @observable facilitiesStudy: Select;
  @observable facilitiesStudyValue: String = '';

  // ENUMs
  @observable statusEnum: Array<EnumTranslate>;
  @IEnumService enumService: IEnumService;

  @IProjectModalService projectModalService: IProjectModalService;

  public _requiredFormFields = [];

  connectedCallback() {
    super.connectedCallback();

    this._requiredFormFields = [
      this.equipmentSupplyAgreements,
      this.epcAgreement,
      this.operationMaintenanceAgreement,
      this.managementServicesAgreement,
      this.interconnectionAgreement,
      this.bopAgreement,
      this.constructionAgreement,
      this.energyManagementAgreement,
      this.feasibilityStudy,
      this.systemImpactStudy,
      this.facilitiesStudy,
    ];

    this.loadEnums();
    this.attachedBlurEvent();
  }

  private attachedBlurEvent() {
    this._requiredFormFields.forEach((field) => {
      field.addEventListener('blur', () => this.checkFieldValidity(field));
      field.addEventListener('change', () => this.checkFieldValidity(field));
    });
  }

  private checkTabCompletionStatus() {
    const tabCompleted = this._requiredFormFields.every((elm) =>
      this.projectModalService.totalCompletedFields.includes(elm)
    );
    this.$emit('contractsTabCompleted', tabCompleted);
  }

  private checkFieldValidity(elm: Select) {
    if (!this.enumService.isDocumentEnumSelected(elm.value)) {
      displayErrorUtil(elm);
      this.amendValidList(elm, false);
    } else {
      clearErrorUtil(elm);
      this.amendValidList(elm, true);
    }
  }

  private amendValidList(field: Select, isValid: boolean) {
    if (isValid) {
      this.projectModalService.addToTotalCompletedFields(field);
    } else {
      this.projectModalService.removeFromTotalCompletedFields(field);
    }
    this.checkTabCompletionStatus();
  }

  private async loadEnums() {
    this.enumService.getEnumValues(CASCADE_ENUM.PROJECT_DRAFT_STATUS).then((response) => {
      if (response.REPLY) {
        this.statusEnum = response.REPLY;
      }
    });
  }

  public populateForm(document: Documents) {
    this.addRequiredFieldsToService();
    this.equipmentSupplyAgreementsValue = document.EQUIPMENT_SUPPLY_AGREEMENTS;
    this.amendValidList(this.equipmentSupplyAgreements, this.enumService.isDocumentEnumSelected(this.equipmentSupplyAgreementsValue));

    this.epcAgreementValue = document.EPC_AGREEMENT;
    this.amendValidList(this.epcAgreement, this.enumService.isDocumentEnumSelected(this.epcAgreementValue));

    this.operationMaintenanceAgreementValue = document.OPERATIONS_MAINTENANCE_AGREEMENT;
    this.amendValidList(this.operationMaintenanceAgreement, this.enumService.isDocumentEnumSelected(this.operationMaintenanceAgreementValue));

    this.managementServicesAgreementValue = document.MANAGEMENT_SERVICES_AGREEMENT;
    this.amendValidList(this.managementServicesAgreement, this.enumService.isDocumentEnumSelected(this.managementServicesAgreementValue));

    this.feasibilityStudyValue = document.FEASIBILITY_STUDY;
    this.amendValidList(this.feasibilityStudy, this.enumService.isDocumentEnumSelected(this.feasibilityStudyValue));

    this.systemImpactStudyValue = document.SYSTEM_IMPACT_STUDY;
    this.amendValidList(this.systemImpactStudy, this.enumService.isDocumentEnumSelected(this.systemImpactStudyValue));

    this.facilitiesStudyValue = document.FACILITIES_STUDY;
    this.amendValidList(this.facilitiesStudy, this.enumService.isDocumentEnumSelected(this.facilitiesStudyValue));

    this.interconnectionAgreementValue = document.INTERCONNECTION_AGREEMENT;
    this.amendValidList(this.interconnectionAgreement, this.enumService.isDocumentEnumSelected(this.interconnectionAgreementValue));

    this.bopAgreementValue = document.BOP_AGREEMENT;
    this.amendValidList(this.bopAgreement, this.enumService.isDocumentEnumSelected(this.bopAgreementValue));

    this.constructionAgreementValue = document.CONSTRUCTION_AGREEMENT;
    this.amendValidList(this.constructionAgreement, this.enumService.isDocumentEnumSelected(this.constructionAgreementValue));

    this.energyManagementAgreementValue = document.ENERGY_MANAGEMENT_AGREEMENT;
    this.amendValidList(this.energyManagementAgreement, this.enumService.isDocumentEnumSelected(this.energyManagementAgreementValue));
  }

  public clearForm() {
    const allFields = [
      this.equipmentSupplyAgreements,
      this.epcAgreement,
      this.operationMaintenanceAgreement,
      this.managementServicesAgreement,
      this.interconnectionAgreement,
      this.bopAgreement,
      this.constructionAgreement,
      this.energyManagementAgreement,
      this.feasibilityStudy,
      this.systemImpactStudy,
      this.facilitiesStudy,
    ];

    allFields.forEach((field) => {
      if (field) {
        clearErrorUtil(field);
      }
    });

    this.equipmentSupplyAgreementsValue = documentStatusDefault;
    this.epcAgreementValue = documentStatusDefault;
    this.operationMaintenanceAgreementValue = documentStatusDefault;
    this.managementServicesAgreementValue = documentStatusDefault;
    this.bopAgreementValue = documentStatusDefault;
    this.constructionAgreementValue = documentStatusDefault;
    this.energyManagementAgreementValue = documentStatusDefault;
    this.feasibilityStudyValue = documentStatusDefault;
    this.systemImpactStudyValue = documentStatusDefault;
    this.facilitiesStudyValue = documentStatusDefault;
    this.interconnectionAgreementValue = documentStatusDefault;

    this.$emit('contractsTabCompleted', false);
  }

  public resetValues() {
    this.equipmentSupplyAgreementsValue = documentStatusDefault;
    this.epcAgreementValue = documentStatusDefault;
    this.operationMaintenanceAgreementValue = documentStatusDefault;
    this.managementServicesAgreementValue = documentStatusDefault;
    this.interconnectionAgreementValue = documentStatusDefault;
    this.bopAgreementValue = documentStatusDefault;
    this.constructionAgreementValue = documentStatusDefault;
    this.energyManagementAgreementValue = documentStatusDefault;
    this.feasibilityStudyValue = documentStatusDefault;
    this.systemImpactStudyValue = documentStatusDefault;
    this.facilitiesStudyValue = documentStatusDefault;
  }

  public addRequiredFieldsToService() {
    // update Service with latest required fields
    this.projectModalService.addToRequiredFormField(this._requiredFormFields);
  }

  public removeRequiredFieldsToService() {
    // update Service with latest required fields
    this.projectModalService.removeFromRequiredFormField(this._requiredFormFields);
  }
}
